import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { CheckOutlined } from '@ant-design/icons';
import classNames from 'classnames';

import Checkbox from '../../checkbox';
import Empty from '../../empty';
import { useSelectPanelStyle } from '../style/select-panel';
import {
  FilterSelectPanelHighlightTextProps,
  FilterSelectPanelProps,
  FilterValueType,
} from '../types';

const SELECT_ALL_KEY = '__select_all__';

const FilterSelectPanelHighlightText: React.FC<FilterSelectPanelHighlightTextProps> = ({
  text,
  highlight,
}) => {
  if (!highlight) return <>{text}</>;
  const idx = text?.toString()?.trim()?.toLowerCase()?.indexOf?.(highlight.toLowerCase());
  if (idx === undefined || idx === -1) return <>{text}</>;
  return (
    <span>
      {text.toString().slice(0, idx)}
      <span className="highlight-text">{text.toString().slice(idx, idx + highlight.length)}</span>
      {text.toString().slice(idx + highlight.length)}
    </span>
  );
};

const FilterSelectPanel: React.FC<FilterSelectPanelProps> = ({
  mode = 'single',
  value,
  noneValue,
  searchValue = '',
  showSelectAll,
  allowUncheck = true,
  options = [],
  onChange,
  onValueChange,
  onClose,
}) => {
  const [wrapSSR, hashId] = useSelectPanelStyle('youlify-filter-select-panel');
  const [selectedValue, setSelectedValue] = useState<FilterValueType>();
  const isSingle = mode === 'single';

  const filteredOptions = useMemo(() => {
    let opts = options;
    if (searchValue) {
      opts = opts.filter((opt) =>
        opt.label?.toString().toLowerCase().includes(searchValue.toLowerCase()),
      );
    }
    if (showSelectAll && !isSingle && !searchValue && opts.length > 0) {
      return [
        {
          value: SELECT_ALL_KEY,
          label: (
            <span>
              {Array.isArray(selectedValue) && selectedValue.length === opts.length
                ? 'Unselect All'
                : 'Select All'}
            </span>
          ),
          disabled: false,
        },
        ...opts,
      ];
    }
    return opts;
  }, [options, searchValue, isSingle, showSelectAll, selectedValue]);

  const handleMenuClick = (optionValue: FilterValueType) => {
    if (isSingle) {
      const isSelected = selectedValue === optionValue;
      if (isSelected && !allowUncheck) return;
      const newValue = isSelected ? noneValue : optionValue;
      setSelectedValue(newValue);
      onChange?.(newValue);
      onValueChange?.(newValue);
      if (!isSelected) onClose?.();
    } else {
      if (optionValue === SELECT_ALL_KEY) {
        const opts = filteredOptions.slice(1).map((opt) => opt.value);
        const allSelected = Array.isArray(selectedValue) && selectedValue.length === opts.length;
        const newValue = allSelected ? (noneValue as any[]) : opts;
        setSelectedValue(newValue);
        onChange?.(newValue);
        onValueChange?.(newValue);
        return;
      }
      let newValue = Array.isArray(selectedValue) ? [...(selectedValue as FilterValueType[])] : [];
      newValue = newValue?.includes(optionValue)
        ? newValue.filter((v) => v !== optionValue)
        : [...newValue, optionValue];
      newValue = newValue.filter((v) => v !== SELECT_ALL_KEY);
      if (newValue.length === 0) newValue = noneValue as any[];
      setSelectedValue(newValue);
      onChange?.(newValue);
      onValueChange?.(newValue);
    }
  };

  useEffect(() => {
    if (value !== selectedValue) {
      if (isSingle) {
        // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
        setSelectedValue(value);
      } else {
        // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
        setSelectedValue(Array.isArray(value) ? value : []);
      }
    }
  }, [value, isSingle]);

  const selectPanelContent = useMemo(
    () => (
      <div className={classNames('youlify-filter-select-panel', hashId)}>
        {filteredOptions.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Data" />
        ) : (
          filteredOptions.map((option, idx) => {
            if (
              showSelectAll &&
              !isSingle &&
              !searchValue &&
              idx === 0 &&
              option.value === SELECT_ALL_KEY
            ) {
              const allSelected =
                Array.isArray(selectedValue) && selectedValue.length === filteredOptions.length - 1;
              const indeterminate =
                Array.isArray(selectedValue) &&
                selectedValue.length > 0 &&
                selectedValue.length < filteredOptions.length - 1;
              return (
                <div
                  key="__select_all__"
                  className={classNames('menu-item', {
                    'menu-item-active': allSelected,
                  })}
                  onClick={() => handleMenuClick(SELECT_ALL_KEY)}
                >
                  <Checkbox indeterminate={indeterminate} checked={allSelected}>
                    {option.label}
                  </Checkbox>
                </div>
              );
            }
            const isChecked = isSingle
              ? (selectedValue as FilterValueType) === option.value
              : (selectedValue as FilterValueType[])?.includes(option.value);
            const labelNode =
              typeof option.label === 'string' && searchValue ? (
                <FilterSelectPanelHighlightText text={option.label} highlight={searchValue} />
              ) : (
                option.label
              );
            if (isSingle) {
              return (
                <div
                  key={option.value?.toString()}
                  className={classNames('menu-item', {
                    'menu-item-disabled': option.disabled,
                    'menu-item-active': isChecked,
                  })}
                  onClick={() => !option.disabled && handleMenuClick(option.value)}
                >
                  {labelNode}
                  {isChecked && <CheckOutlined className="check-icon" />}
                </div>
              );
            }
            return (
              <div
                key={option.value?.toString()}
                className={classNames('menu-item', {
                  'menu-item-disabled': option.disabled,
                  'menu-item-active': isChecked,
                })}
                onClick={() => !option.disabled && handleMenuClick(option.value)}
              >
                <Checkbox checked={isChecked} disabled={option.disabled}>
                  {labelNode}
                </Checkbox>
              </div>
            );
          })
        )}
      </div>
    ),
    [filteredOptions, selectedValue, isSingle, searchValue, hashId],
  );

  return wrapSSR(selectPanelContent);
};

export default FilterSelectPanel;
